import { Link } from 'react-router-dom';
import { AlertCircle, ArrowDownRight, ArrowUpRight, CheckCircle2, HelpCircle, Info, Minus, Search } from 'lucide-react';
import { PLANS } from '../../plans';
import './ui.css';

const classes = (...values) => values.filter(Boolean).join(' ');

/* ── Buttons ────────────────────────────────────────────────── */
export function Button({ variant = 'primary', size, block, to, href, children, className, loading, ...rest }) {
  const style = classes(
    'dt-btn',
    variant !== 'primary' && `is-${variant}`,
    size === 'sm' && 'is-sm',
    block && 'is-block',
    className,
  );
  const content = <>{loading && <span className="dt-spinner" aria-hidden="true" />}{children}</>;
  if (to) return <Link to={to} className={style} {...rest}>{content}</Link>;
  if (href) return <a href={href} className={style} {...rest}>{content}</a>;
  return <button type="button" className={style} disabled={loading || rest.disabled} {...rest}>{content}</button>;
}

/* ── Headers ────────────────────────────────────────────────── */
export function Breadcrumb({ items }) {
  return (
    <nav className="dt-breadcrumb" aria-label="Breadcrumb">
      {items.map((item, index) => (
        <span key={item.label}>
          {index > 0 && <span aria-hidden="true"> / </span>}
          {item.to ? <Link to={item.to}>{item.label}</Link> : <span aria-current="page">{item.label}</span>}
        </span>
      ))}
    </nav>
  );
}

export function PageHeader({ title, description, breadcrumb, actions, id }) {
  return (
    <header className="dt-page-header">
      <div className="dt-page-header-copy">
        {breadcrumb && <Breadcrumb items={breadcrumb} />}
        <h1 className="dt-title" id={id}>{title}</h1>
        {description && <p className="dt-support">{description}</p>}
      </div>
      {actions && <div className="dt-page-header-actions">{actions}</div>}
    </header>
  );
}

export function SectionHeader({ title, description, actions, as: Tag = 'h2' }) {
  return (
    <div className="dt-section-header">
      <div>
        <Tag className="dt-section-title">{title}</Tag>
        {description && <p className="dt-support">{description}</p>}
      </div>
      {actions}
    </div>
  );
}

/* ── Badges ─────────────────────────────────────────────────── */
export function Badge({ tone = 'neutral', children, className }) {
  return <span className={classes('dt-badge', `is-${tone}`, className)}>{children}</span>;
}

export function PlanBadge({ plan }) {
  const tone = plan === 'launch' ? 'neutral' : plan === 'revenue_partner' ? 'info' : 'primary';
  return <Badge tone={tone} className="is-plan">{PLANS[plan]?.name || 'Launch'}</Badge>;
}

const STATUS_TONE = {
  draft: 'neutral', scheduled: 'info', active: 'primary', complete: 'success', archived: 'neutral',
  new: 'info', contacted: 'primary', qualified: 'primary', won: 'success', lost: 'neutral',
};

export function StatusBadge({ status }) {
  return <Badge tone={STATUS_TONE[status] || 'neutral'}>{String(status || '').replace(/_/g, ' ')}</Badge>;
}

/* ── Metrics ────────────────────────────────────────────────── */
export function StatChange({ value, suffix = '', label }) {
  if (value === null || value === undefined) return label ? <span className="dt-change is-flat">{label}</span> : null;
  const direction = value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
  const Icon = direction === 'up' ? ArrowUpRight : direction === 'down' ? ArrowDownRight : Minus;
  return (
    <span className={`dt-change is-${direction}`}>
      <Icon size={14} aria-hidden="true" />
      {value > 0 ? '+' : ''}{value}{suffix}
      {label && <span className="dt-support">{label}</span>}
    </span>
  );
}

export function MetricCard({ label, value, unit, help, change, footer, to, icon: Icon }) {
  const body = (
    <>
      <span className="dt-metric-head">
        <span>{Icon && <Icon size={15} aria-hidden="true" />}{label}{help && <HelpTip text={help} label={`About ${label}`} />}</span>
      </span>
      <span className="dt-metric-value">{value}{unit && <small>{unit}</small>}</span>
      <span className="dt-metric-foot">{change}{footer && <span>{footer}</span>}</span>
    </>
  );
  if (to) return <Link to={to} className="dt-metric">{body}</Link>;
  return <div className="dt-metric">{body}</div>;
}

export function UsageMeter({ label, used, limit }) {
  const percent = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const tone = percent >= 100 ? 'is-full' : percent >= 80 ? 'is-high' : '';
  return (
    <div className="dt-usage">
      <div className="dt-usage-head"><span>{label}</span><span><b>{used}</b> / {limit}</span></div>
      <div className="dt-usage-track" role="progressbar" aria-valuenow={used} aria-valuemin={0} aria-valuemax={limit} aria-label={label}>
        <div className={classes('dt-usage-fill', tone)} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

/* ── Forms ──────────────────────────────────────────────────── */
export function FormField({ label, help, required, children, wide, id }) {
  return (
    <label className={classes('dt-field', wide && 'dt-field-wide')} htmlFor={id}>
      <span>{label}{required && <em className="dt-field-required">Required</em>}{help && <HelpTip text={help} label={`About ${label}`} />}</span>
      {children}
      {help && <span className="dt-field-help">{help}</span>}
    </label>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Search', label, id }) {
  return (
    <div className="dt-search">
      <Search size={15} aria-hidden="true" />
      <input id={id} type="search" value={value} onChange={onChange} placeholder={placeholder} aria-label={label || placeholder} />
    </div>
  );
}

export function FilterChips({ options, value, onChange, label }) {
  return (
    <div className="dt-chips" role="group" aria-label={label}>
      {options.map(option => (
        <button type="button" key={option.id} className="dt-chip" aria-pressed={value === option.id} onClick={() => onChange(option.id)}>
          {option.label}{option.count !== undefined && ` (${option.count})`}
        </button>
      ))}
    </div>
  );
}

/* ── Feedback ───────────────────────────────────────────────── */
export function Alert({ tone = 'info', children }) {
  const Icon = tone === 'danger' ? AlertCircle : tone === 'success' ? CheckCircle2 : Info;
  return <div className={`dt-alert is-${tone}`} role={tone === 'danger' ? 'alert' : 'status'}><Icon size={15} aria-hidden="true" /><span>{children}</span></div>;
}

export function EmptyState({ icon: Icon, title, description, actions }) {
  return (
    <div className="dt-empty">
      {Icon && <span className="dt-empty-icon"><Icon size={20} aria-hidden="true" /></span>}
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {actions && <div className="dt-empty-actions">{actions}</div>}
    </div>
  );
}

export function Skeleton({ height, width, className }) {
  return <div className={classes('dt-skeleton', className)} style={{ height, width }} aria-hidden="true" />;
}

export function SkeletonList({ rows = 4, height = 44 }) {
  return <div className="dt-stack-2" aria-hidden="true">{Array.from({ length: rows }, (_, index) => <Skeleton key={index} height={height} />)}</div>;
}

export function HelpTip({ text, label }) {
  return (
    <span className="dt-help">
      <button type="button" aria-label={label || 'More information'} title={text}><HelpCircle size={14} aria-hidden="true" /></button>
    </span>
  );
}

/* ── Upgrade gate ───────────────────────────────────────────── */
export function UpgradeGate({ title, description, plan, actions }) {
  return (
    <div className="dt-upgrade">
      <PlanBadge plan={plan} />
      <h3>{title}</h3>
      <p>{description}</p>
      <div className="dt-upgrade-actions">
        {actions || <>
          <Button to="/#pricing">Upgrade to {PLANS[plan]?.name}</Button>
          <Button variant="secondary" to="/#pricing">Compare plans</Button>
        </>}
      </div>
    </div>
  );
}
